import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const PRESET_COLORS = [
  "#22C55E", "#EF4444", "#F59E0B", "#F97316", "#3B82F6",
  "#6B7280", "#DC2626", "#10B981", "#8B5CF6", "#06B6D4",
  "#EC4899", "#14B8A6", "#A855F7", "#84CC16",
];

export interface TagFormData {
  name: string;
  description: string;
  color: string;
}

interface TagFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  form: TagFormData;
  onFormChange: (form: TagFormData) => void;
  onSave: () => void;
  saving: boolean;
  isEdit: boolean;
}

const TagFormDialog: React.FC<TagFormDialogProps> = ({
  open,
  onOpenChange,
  form,
  onFormChange,
  onSave,
  saving,
  isEdit,
}) => {
  const update = (patch: Partial<TagFormData>) => {
    onFormChange({ ...form, ...patch });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edit Tag" : "New Tag"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Name</Label>
            <Input
              value={form.name}
              onChange={(e) => update({ name: e.target.value })}
              placeholder="e.g. Motivated Seller, Hot Lead"
            />
          </div>
          <div className="space-y-2">
            <Label>Description (optional)</Label>
            <Input
              value={form.description}
              onChange={(e) => update({ description: e.target.value })}
              placeholder="When to use this tag"
            />
          </div>
          <div className="space-y-2">
            <Label>Color</Label>
            <div className="flex flex-wrap gap-2">
              {PRESET_COLORS.map((color) => (
                <button
                  key={color}
                  onClick={() => update({ color })}
                  className="h-7 w-7 rounded-full border-2 transition-all"
                  style={{
                    backgroundColor: color,
                    borderColor: form.color === color ? "white" : "transparent",
                    boxShadow: form.color === color ? `0 0 0 2px ${color}` : "none",
                  }}
                />
              ))}
            </div>
            <div className="flex items-center gap-2 mt-1">
              <Label className="text-xs text-muted-foreground">Custom:</Label>
              <Input
                type="color"
                value={form.color}
                onChange={(e) => update({ color: e.target.value })}
                className="h-7 w-10 p-0 border-0 cursor-pointer"
              />
              <span className="text-xs text-muted-foreground font-mono">
                {form.color}
              </span>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={saving}>
            {saving ? "Saving..." : isEdit ? "Update" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TagFormDialog;
